import { useEffect, useState } from 'react';
import { useFocusWithin } from '@react-aria/interactions';
import styled from 'styled-components';
import { useField } from 'formik';
import { Box } from 'reflexbox';
import { useTranslation } from 'react-i18next';
import * as _ from 'lodash';

import * as graphql from 'generated/graphql';
import Field, { FieldType } from 'packages/kakapo-ui-kit/components/forms/controls/Field';
import Select from 'packages/kakapo-ui-kit/components/forms/controls//Select';
import { ParagraphSmall } from 'packages/kakapo-ui-kit/components/common/Paragraph';
import usePreferredTranslation from 'packages/kakapo-ui-application/hooks/usePreferredTranslation';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import { Value } from 'packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableFormValue';
import useSupportedLanguageIDs from 'packages/kakapo-ui-application/hooks/useSupportedLanguageIDs';

const Container = styled.div`
	display: flex;
	align-items: center;
	margin-bottom: 10px;
`;

const LanguageBox = styled.div`
	width: 75px;
	flex-grow: 0;
	flex-shrink: 0;
	margin-right: 10px;
`;

const AddButton = styled(ParagraphSmall)`
	color: ${(props) => props.theme.colors.slateBlue};
	cursor: pointer;

	:hover {
		text-decoration: underline;
	}
`;

export type ArrayElement<ArrayType extends readonly unknown[]> =
	ArrayType extends readonly (infer ElementType)[] ? ElementType : never;

const MultilingualTranslation = (props: {
	autofocus: boolean;
	languageID: string;
	name: string;
	availableLanguageIDs: string[];
	text: string;
}) => {
	const { language } = useLocalize();
	const { languageID, name, availableLanguageIDs } = props;

	const [field, , { setValue }] = useField<ArrayElement<graphql.LocalizedString>>(name);

	const languages = _.sortBy(
		[
			{
				id: props.languageID,
				label: props.languageID.split('_')[0].toUpperCase(),
			},
			...availableLanguageIDs.map((id) => ({
				id,
				label: id.split('_')[0].toUpperCase(),
			})),
		],
		[({ id }) => id, ({ id }) => id !== language.id],
	);

	return (
		<Container>
			<LanguageBox>
				<Select
					unspaced
					small
					options={languages}
					value={languages.find((item) => item.id === languageID)}
					onChange={(value) => {
						if (value) {
							setValue({ ...field.value, languageID: value.id });
						}
					}}
				/>
			</LanguageBox>
			<Field
				autoFocus={props.autofocus}
				name={`${name}.text`}
				type={FieldType.Text}
				fullWidth
				unspaced
				small
				hideError
			/>
		</Container>
	);
};

export default function Multilingual(props: { name: string; requiresFocus?: boolean }) {
	const { name } = props;

	const { t } = useTranslation();
	const { language } = useLocalize();

	const [field, , { setValue }] = useField<graphql.LocalizedString>(name);

	const value = field.value ?? [];

	const supportedLanguages = useSupportedLanguageIDs();

	const availableLanguageIDs =
		_.sortBy(
			supportedLanguages?.filter((languageID) => {
				return !value.find((translation) => translation.languageID === languageID);
			}),
			(id) => id !== language.id,
		) ?? [];

	const [added, setAdded] = useState(false);

	const addTranslation = () => {
		if (!supportedLanguages) {
			return;
		}

		setValue(
			value.concat([
				{
					languageID: availableLanguageIDs[0],
					text: '',
				},
			]),
		);

		setAdded(true);
	};

	const vacuumTranslations = () => {
		const newValue = value.filter((value) => value.text.length > 0);
		setValue(newValue.length > 0 ? newValue : value);
	};

	const { translate } = usePreferredTranslation();

	const [focus, setFocus] = useState(false);

	const { focusWithinProps } = useFocusWithin({
		onBlurWithin: () => {
			setFocus(false);
			vacuumTranslations();
		},
	});

	useEffect(() => {
		if (value.length === 0) {
			addTranslation();
		}
	}, [!!supportedLanguages]);

	if (!focus && props.requiresFocus) {
		return (
			<Value
				onClick={() => {
					if (value.length === 0) {
						addTranslation();
					}

					setFocus(true);
				}}
			>
				{translate(value) || '–'}
			</Value>
		);
	}

	return (
		<div {...focusWithinProps}>
			{(field.value ?? []).map(({ languageID, text }, index) => (
				<MultilingualTranslation
					autofocus={(focus || !props.requiresFocus) && (index === 0 || added)}
					key={index}
					name={`${name}[${index}]`}
					languageID={languageID}
					availableLanguageIDs={availableLanguageIDs}
					text={text}
				/>
			))}
			{availableLanguageIDs.length > 0 && (
				<Box
					marginLeft={85}
					onClick={addTranslation}
					onMouseDown={(event) => {
						event.preventDefault();
						event.stopPropagation();
					}}
				>
					<AddButton>+ {t('common.addTranslation')}</AddButton>
				</Box>
			)}
		</div>
	);
}
